<div class="video-preview-wrapper">
  <!-- Reusable Header Component -->
  <app-preview-header
    [fileData]="model"
    [showMetadata]="true"
    [showDownload]="true"
    [showComments]="true"
    [commentsActive]="showCommentsSidebar"
    [showFullscreen]="true"
    [fullscreenActive]="isFullscreen"
    [showMinimize]="true"
    (comments)="toggleCommentsSidebar()"
    (fullscreen)="toggleFullscreen()"
    (minimize)="minimizeToMiniPlayer()"
    (download)="downloadMedia($event)"
    (close)="closeDialog()">
  </app-preview-header>

  <div class="video-main">
  <!-- Video Player Body -->
  <div class="video-body" [class.comment-armed]="commentArmed">
    <!-- Loading Overlay -->
    @if (isLoading) {
      <app-preview-loading
        [message]="'Loading video...'"
        [overlay]="true">
      </app-preview-loading>
    }

    <!-- Video Element (hidden controls) -->
    <video
      #videoPlayer
      [src]="getMediaUrl()"
      class="video-content"
      (loadedmetadata)="onVideoLoaded()"
      (timeupdate)="onTimeUpdate()"
      (play)="onPlay()"
      (pause)="onPause()"
      (waiting)="onWaiting()"
      (canplay)="onCanPlay()"
      (error)="onVideoError()"
      (click)="onVideoClick()"
      preload="metadata"
      [attr.aria-label]="'Video player for ' + model?.original_name">
      <p>Your browser does not support HTML5 video.</p>
    </video>

    <!-- Custom Video Controls -->
    @if (!isLoading) {
      <div class="video-controls" [class.hidden]="!showControls">
        <!-- Timeline -->
        <div class="timeline-container">
          <div
            class="timeline-bar"
            (click)="onTimelineClick($event)"
            (mousedown)="onTimelineDragStart($event)">
            <!-- Buffered Progress -->
            <div class="timeline-buffered" [style.width.%]="getBufferedProgress()"></div>
            <!-- Current Progress -->
            <div class="timeline-progress" [style.width.%]="getTimelineProgress()">
              <div class="timeline-thumb"></div>
            </div>
          </div>
        </div>
        <!-- Control Bar -->
        <div class="control-bar">
          <!-- Left Controls -->
          <div class="control-group left">
            <!-- Play/Pause Button -->
            <button
              class="control-btn play-btn"
              (click)="togglePlay()"
              [attr.aria-label]="isPlaying ? 'Pause' : 'Play'"
              matTooltip="{{ isPlaying ? 'Pause (Space)' : 'Play (Space)' }}">
              <i class="fas" [class.fa-pause]="isPlaying" [class.fa-play]="!isPlaying"></i>
            </button>
            <!-- Skip Backward -->
            <button
              class="control-btn"
              (click)="skip(-5)"
              aria-label="Skip backward 5 seconds"
              matTooltip="Skip -5s (←)">
              <i class="fas fa-undo"></i>
              <span class="skip-text">5</span>
            </button>
            <!-- Skip Forward -->
            <button
              class="control-btn"
              (click)="skip(5)"
              aria-label="Skip forward 5 seconds"
              matTooltip="Skip +5s (→)">
              <span class="skip-text">5</span>
              <i class="fas fa-redo"></i>
            </button>
            <!-- Time Display -->
            <div class="time-display">
              <span class="current-time">{{ formatTime(currentTime) }}</span>
              <span class="time-divider">/</span>
              <span class="duration-time">{{ formatTime(duration) }}</span>
            </div>
          </div>
          <!-- Right Controls -->
          <div class="control-group right">
            <!-- Volume Control -->
            <div class="volume-control">
              <button
                class="control-btn"
                (click)="toggleMute()"
                [attr.aria-label]="isMuted ? 'Unmute' : 'Mute'"
                matTooltip="Mute (M)">
                <i class="fas" [class]="'fas ' + getVolumeIcon()"></i>
              </button>
              <input
                type="range"
                class="volume-slider"
                min="0"
                max="1"
                step="0.01"
                [value]="volume"
                (input)="setVolume($any($event.target).value)"
                aria-label="Volume"
                matTooltip="Volume (↑↓)">
            </div>
            <!-- Playback Speed -->
            <div class="speed-control">
              <button
                class="control-btn speed-btn"
                [matMenuTriggerFor]="speedMenu"
                aria-label="Playback speed"
                matTooltip="Playback Speed">
                <span class="speed-text">{{ playbackRate }}x</span>
              </button>
              <mat-menu #speedMenu="matMenu" xPosition="before">
                @for (rate of playbackRateOptions; track rate) {
                  <button
                    mat-menu-item
                    (click)="setPlaybackRate(rate)"
                    [class.active]="playbackRate === rate">
                    {{ rate }}x
                  </button>
                }
              </mat-menu>
            </div>
          </div>
        </div>
      </div>
    }
  </div>

  <!-- Comment sidebar (slides in beside the video) -->
  <div class="video-comment-drawer" *ngIf="showCommentsSidebar">
    <app-doc-comment-sidebar
      [comments]="comments"
      [activeCommentId]="activeCommentId"
      [showTabs]="false"
      [showAddButton]="true"
      [showStatusPriority]="false"
      (addComment)="startAddComment()"
      (selectComment)="selectComment($event)"
      (addReply)="onCommentReply($event)"
      (updateStatus)="onCommentStatus($event)"
      (updatePriority)="onCommentPriority($event)"
      (deleteComment)="deleteComment($event)"
      (close)="toggleCommentsSidebar()">
    </app-doc-comment-sidebar>
  </div>
  </div><!-- /.video-main -->

  <!-- Add-comment popup, anchored to the current playback time -->
  <div class="video-comment-popup" *ngIf="newCommentTime !== null">
    <div class="video-comment-popup__title">
      <mat-icon>add_comment</mat-icon> Comment at <strong>{{ formatTime(newCommentTime || 0) }}</strong>
    </div>
    <textarea rows="3" [(ngModel)]="newCommentText" placeholder="Write a comment…"></textarea>
    <div class="video-comment-popup__actions">
      <button type="button" class="video-comment-popup__btn" (click)="cancelComment()">Cancel</button>
      <button type="button" class="video-comment-popup__btn is-primary" [disabled]="!newCommentText.trim()"
              (click)="addComment()">Comment</button>
    </div>
  </div>

  <!-- Keyboard Shortcuts Hint (Optional) -->
  @if (showControls && !isPlaying) {
    <div class="keyboard-hint">
      <div class="hint-item">
        <kbd>Space</kbd> Play/Pause
      </div>
      <div class="hint-item">
        <kbd>←</kbd><kbd>→</kbd> Skip
      </div>
      <div class="hint-item">
        <kbd>↑</kbd><kbd>↓</kbd> Volume
      </div>
      <div class="hint-item">
        <kbd>F</kbd> Fullscreen
      </div>
    </div>
  }
</div>
